<div class="space-y-5">
  <!-- Address & Header Information -->
  @if (!hideAddress()) {
    <div
      class="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-2 border-b border-gray-6/60"
    >
      <div>
        <span class="text-xs font-bold text-gray-11 uppercase tracking-wider block mb-1"
          >Localisation du bien</span
        >
        <div class="text-base font-semibold text-gray-12 flex items-center gap-1.5">
          <mat-icon class="text-indigo-11 text-lg! w-5! h-5!">location_on</mat-icon>
          <span>{{ location().address.street || 'Adresse non renseignée' }}</span>
        </div>
        <div class="text-sm text-gray-11 font-medium ml-6">
          {{ location().address.zipCode }} {{ location().address.city }},
          {{ location().address.country | countryIso2 }}
          @if (location().address.region) {
            <span class="text-gray-10">({{ location().address.region }})</span>
          }
        </div>
      </div>

      <!-- Geolocation Precision Pill -->
      <div class="flex items-center gap-2">
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-3 text-gray-11 border border-gray-6"
        >
          <mat-icon class="text-sm! w-4! h-4! text-indigo-10">radar</mat-icon>
          <span>Précision : {{ location().geolocationType | etxeEnumDisplay: 'geolocationType' }}</span>
        </span>
      </div>
    </div>
  }

  @if (location().coordinates?.latitude && location().coordinates?.longitude) {
    <!-- POI Filter Layout & Category Toolbar -->
    @if (showPoiToolbar()) {
      <div class="rounded-2xl border border-gray-6 bg-gray-2/60 p-4 space-y-3 shadow-xs">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold uppercase tracking-wider text-gray-11">
              Points d'intérêt & commodités à proximité (1 km)
            </span>
            @if (loadingPois()) {
              <mat-spinner diameter="16" class="inline-block text-indigo-10"></mat-spinner>
            }
          </div>

          <button
            type="button"
            (click)="toggleAll()"
            class="text-xs font-semibold text-indigo-11 hover:text-indigo-12 transition-colors cursor-pointer px-2 py-1 rounded-md hover:bg-indigo-3/50"
          >
            {{ allActive() ? 'Tout masquer' : 'Tout afficher' }}
          </button>
        </div>

        <!-- Categories Filter Chips -->
        <div class="flex flex-wrap gap-2 pt-1">
          @for (cat of categories; track cat.id) {
            <button
              type="button"
              (click)="toggleCategory(cat.id)"
              class="group inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all cursor-pointer select-none"
              [class]="
                isCategoryActive(cat.id)
                  ? cat.bgColor +
                    ' ' +
                    cat.textColor +
                    ' ' +
                    cat.borderColor +
                    ' shadow-xs ring-1 ring-black/5 font-semibold'
                  : 'bg-white/80 dark:bg-gray-1/80 border-gray-6 text-gray-10 hover:border-gray-8 hover:text-gray-12 opacity-65 hover:opacity-100'
              "
            >
              <mat-icon class="text-base! w-4! h-4! transition-transform group-hover:scale-110">
                {{ cat.icon }}
              </mat-icon>
              <span>{{ cat.label }}</span>
              <span
                class="px-1.5 py-0.2 rounded-full text-[10px] font-bold transition-colors"
                [class]="isCategoryActive(cat.id) ? cat.badgeBg + ' text-white' : 'bg-gray-4 text-gray-11'"
              >
                {{ getCategoryDisplayCount(cat.id) }}
              </span>
            </button>
          }
        </div>
      </div>
    }

    <!-- Map Container -->
    <div
      class="relative w-full h-80 md:h-[400px] rounded-2xl overflow-hidden bg-gray-2 border border-gray-6 shadow-sm"
    >
      <aura-open-street-map
        [lat]="location().coordinates?.latitude ?? 0"
        [lng]="location().coordinates?.longitude ?? 0"
        [radius]="getMapRadius(location().geolocationType)"
        [pois]="pois()"
        [activeCategories]="activeCategories()"
        [scrollWheelZoom]="false"
        [showMarker]="effectiveShowMarker()"
      ></aura-open-street-map>
    </div>

    <!-- Nearest Amenities Summary (Proximity highlights) -->
    @if (showProximityList() && nearestActivePois().length > 0) {
      <div class="space-y-3 pt-2">
        <span class="text-xs font-bold uppercase tracking-wider text-gray-11 block">
          À proximité immédiate à pied
        </span>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          @for (item of nearestActivePois(); track item.id) {
            <div
              class="flex items-center justify-between p-2.5 rounded-xl border border-gray-6 bg-white/70 dark:bg-gray-1/70 hover:border-indigo-6 hover:shadow-xs transition-all"
            >
              <div class="flex items-center gap-2.5 min-w-0 pr-2">
                <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-3 text-xs">
                  @switch (item.category) {
                    @case ('shops') {
                      🛒
                    }
                    @case ('education') {
                      🎓
                    }
                    @case ('transport') {
                      🚆
                    }
                    @case ('leisure') {
                      ⚽
                    }
                    @case ('services') {
                      🏛️
                    }
                    @case ('health') {
                      🏥
                    }
                    @default {
                      📍
                    }
                  }
                </span>
                <div class="truncate">
                  <div class="text-xs font-semibold text-gray-12 truncate">{{ item.name }}</div>
                  @if (item.details) {
                    <div class="text-[10px] text-gray-10 truncate">{{ item.details }}</div>
                  }
                </div>
              </div>

              <div class="shrink-0 text-right">
                <span
                  class="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-11 bg-indigo-3/60 px-2 py-0.5 rounded-md"
                >
                  <mat-icon class="text-xs! w-3.5! h-3.5!">directions_walk</mat-icon>
                  <span>{{ getWalkMinutes(item.distanceMeters) }} min</span>
                </span>
                <div class="text-[10px] text-gray-10 font-medium mt-0.5">{{ item.distanceMeters }} m</div>
              </div>
            </div>
          }
        </div>
      </div>
    }
  }
</div>
